import { FC, useEffect, useRef } from "react";
import { useHistory } from "react-router";

import { Alert, Heading, Text } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { PaymentFailedError } from "hooks/use-payment-failure";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { SubscriptionRoutes } from "routes/subscription";

export enum PaymentFailedLocation {
  Invite = "invite",
  InviteLC = "invite_lc",
  Onboarding = "onboarding",
  SubUpdate = "sub_update",
  RoleUpgrade = "role_upgrade",
}

const readableLocation = {
  [PaymentFailedLocation.Invite]: "Agent invitation",
  [PaymentFailedLocation.InviteLC]: "LiveChat agent invitation",
  [PaymentFailedLocation.Onboarding]: "Onboarding",
  [PaymentFailedLocation.SubUpdate]: "Subscription update",
  [PaymentFailedLocation.RoleUpgrade]: "Role upgrade",
};

const readableError = {
  [PaymentFailedError.CardExpired]: {
    title: "Your card has expired",
    message:
      "Check your card's expiration date and update your payment method with a current card. For further assistance, contact your card issuer.",
  },
  [PaymentFailedError.InsufficientFunds]: {
    title: "Insufficient funds",
    message:
      "Check if you have adequate funds, a valid credit card, sufficient credit card limits, or correct billing details. In other cases, contact your bank.",
  },
  [PaymentFailedError.Generic]: {
    title: "Your transaction failed",
    message:
      "Check if you have adequate funds, a valid credit card, sufficient credit card limits, or correct billing details. In other cases, contact your bank for further assistance.",
  },
};

interface IProps {
  location: PaymentFailedLocation;
  error?: PaymentFailedError;
  autoScroll?: boolean;
}

export const PaymentFailedAlert: FC<IProps> = ({ location, error, autoScroll }) => {
  const history = useHistory();
  const alertRef = useRef<HTMLDivElement>(null);
  const errorOrDefault = error || PaymentFailedError.Generic;

  useEffect(() => {
    if (autoScroll && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, []);

  return (
    <Alert
      kind="error"
      primaryButton={{
        label: "Update payment method",
        handleClick: () => {
          trackEngagement("Transaction failed: Go to billing details CTA clicked", EventPlace.Agents, {
            location: readableLocation[location],
          });
          history.push(SubscriptionRoutes.AccountDetails);
        },
      }}
      data-testid="payment-failed-alert"
    >
      <div ref={alertRef}></div>
      <Heading size="sm">{readableError[errorOrDefault].title}</Heading>
      <Text>{readableError[errorOrDefault].message}</Text>
    </Alert>
  );
};
